// Teacher/admin-facing REST service for hallpass-backend's passes-api pass
// moderation actions (POST /api/passes/:id/approve | /deny — see
// src/api/config.js's PASSES_API_BASE and src/api/http.js's passesApi()).
//
// These two routes are TEACHER+ only (requireMinRole(TEACHER), see
// apps/passes-api/src/routes/passes.ts), which is why they live here rather
// than in the student-facing studentPassesApi.js (whose header explicitly
// notes approve/deny are "not exported here"). return/cancel stay in
// studentPassesApi.js since a student may perform them on their own pass.

import { passesApi } from './http';
import type { components } from './types.gen';

type PassResponse = components['schemas']['PassResponse'];
type ApprovePassBody = components['schemas']['ApprovePassBody'];
type DenyPassBody = components['schemas']['DenyPassBody'];

/**
 * Approve a PENDING pass (TEACHER+). Transitions PENDING -> ACTIVE (or
 * WAITING if the destination is at capacity). An optional approverNote is
 * recorded on the pass; omitted entirely when not provided (the backend's
 * approvePassBody makes it optional, max 1000 chars).
 *
 * @param {number|string} passId
 * @param {{approverNote?: string}} [options]
 * @returns {Promise<object>} the updated pass
 * @throws {import('./http').ApiError} 409 "Pass is not in PENDING status" /
 *   "Pass is no longer valid for the current period"; 404 if not found in the
 *   caller's school
 */
export function approvePass(
  passId: number | string,
  { approverNote }: ApprovePassBody = {}
): Promise<PassResponse> {
  const body: ApprovePassBody = approverNote !== undefined ? { approverNote } : {};
  return passesApi<PassResponse>(`/api/passes/${passId}/approve`, { method: 'POST', body });
}

/**
 * Deny a PENDING pass (TEACHER+). Transitions PENDING -> DENIED. An optional
 * denierNote is recorded on the pass; omitted entirely when not provided (the
 * backend's denyPassBody makes it optional, max 1000 chars). Deny is
 * PENDING-only by design — WAITING passes are cancelled, not denied.
 *
 * @param {number|string} passId
 * @param {{denierNote?: string}} [options]
 * @returns {Promise<object>} the updated pass (status DENIED)
 * @throws {import('./http').ApiError} 409 "Pass must be PENDING to deny"; 404
 *   if not found in the caller's school
 */
export function denyPass(
  passId: number | string,
  { denierNote }: DenyPassBody = {}
): Promise<PassResponse> {
  const body: DenyPassBody = denierNote !== undefined ? { denierNote } : {};
  return passesApi<PassResponse>(`/api/passes/${passId}/deny`, { method: 'POST', body });
}
